import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Stack,
  Card,
  TextField,
  Button,
  ButtonGroup,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TableContainer,
  Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Chip from '@mui/material/Chip';
import { getFirestore, collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { CALIBER_OPTIONS } from '../utils/calibers';

const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';
const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export default function RecurringReservationsPage() {
  const [categories, setCategories] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [editingId, setEditingId] = useState(null);
  const [memberEmail, setMemberEmail] = useState('');
  const [memberPhone, setMemberPhone] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [selectedLaneIds, setSelectedLaneIds] = useState([]);
  const [startTime, setStartTime] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [daysOfWeek, setDaysOfWeek] = useState({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('Cliente especial - Rifle');
  // Calibres que usa el cliente (registro del campo de tiro) — se copian a
  // cada reservación generada por la regla.
  const [selectedCalibers, setSelectedCalibers] = useState([]);
  const [otherCaliber, setOtherCaliber] = useState('');
  const [formError, setFormError] = useState(null);

  const toggleCaliber = (cal) => {
    setSelectedCalibers((prev) => (prev.includes(cal) ? prev.filter((c) => c !== cal) : [...prev, cal]));
  };

  const fetchData = async () => {
    const db = getFirestore();
    const catSnap = await getDocs(query(collection(db, 'reservation_categories'), where('active', '==', true)));
    setCategories(catSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

    const lanesSnap = await getDocs(query(collection(db, 'lanes'), where('active', '==', true)));
    setLanes(lanesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

    const recSnap = await getDocs(query(collection(db, 'recurring_reservations'), orderBy('createdAt', 'desc')));
    setRecurring(recSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => { fetchData(); }, []);

  const resetForm = () => {
    setEditingId(null);
    setMemberEmail('');
    setMemberPhone('');
    setCategoryId('');
    setSelectedLaneIds([]);
    setStartTime('');
    setDurationMinutes(60);
    setDaysOfWeek({});
    setStartDate('');
    setEndDate('');
    setReason('Cliente especial - Rifle');
    setSelectedCalibers([]);
    setOtherCaliber('');
    setFormError(null);
  };

  const handleOpen = () => { resetForm(); setOpen(true); };
  const handleClose = () => { setOpen(false); resetForm(); };

  const handleEdit = (r) => {
    resetForm();
    setEditingId(r.id);
    setMemberEmail(r.memberEmail || '');
    setMemberPhone(r.memberPhone || '');
    setCategoryId(r.categoryId || '');
    // backwards compat: laneId (single) o lanes (array)
    if (Array.isArray(r.lanes) && r.lanes.length) setSelectedLaneIds(r.lanes);
    else if (r.laneId) setSelectedLaneIds([r.laneId]);
    else setSelectedLaneIds([]);
    setStartTime(r.startTime || '');
    setDurationMinutes(r.durationMinutes || 60);
    const dows = {};
    (r.daysOfWeek || []).forEach((i) => { dows[i] = true; });
    setDaysOfWeek(dows);
    setStartDate(r.startDate || '');
    setEndDate(r.endDate || '');
    setReason(r.reason || '');
    // Los calibres guardados que estén en la lista van como chips; el resto al campo "Otro"
    const savedCalibers = Array.isArray(r.calibers) ? r.calibers : [];
    setSelectedCalibers(savedCalibers.filter((c) => CALIBER_OPTIONS.includes(c)));
    setOtherCaliber(savedCalibers.filter((c) => !CALIBER_OPTIONS.includes(c)).join(', '));
    setOpen(true);
  };

  const toggleLaneSelected = (id) => {
    setSelectedLaneIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  // Filtrar lanes por la categoría seleccionada
  const filteredLanes = useMemo(() => {
    if (!categoryId) return [];
    return lanes.filter((l) => Array.isArray(l.categoryIds) && l.categoryIds.includes(categoryId));
  }, [lanes, categoryId]);

  // Limpiar selección de lanes que ya no apliquen al cambiar de categoría
  useEffect(() => {
    if (!categoryId) {
      setSelectedLaneIds([]);
      return;
    }
    const validIds = new Set(filteredLanes.map((l) => l.id));
    setSelectedLaneIds((prev) => prev.filter((id) => validIds.has(id)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, lanes]);

  const setDows = (arr) => {
    const next = {};
    arr.forEach((i) => { next[i] = true; });
    setDaysOfWeek(next);
  };

  const handleDowToggleChange = (event, newDows) => {
    const next = {};
    newDows.forEach((i) => { next[i] = true; });
    setDaysOfWeek(next);
  };

  const selectedDows = Object.entries(daysOfWeek).filter(([, v]) => v).map(([k]) => Number(k));

  const handleSave = async () => {
    const dows = Object.entries(daysOfWeek).filter(([, v]) => v).map(([k]) => Number(k));
    if (!memberEmail || !categoryId || !startTime || dows.length === 0 || !startDate || !endDate) {
      setFormError('Completa todos los campos.');
      return;
    }
    // En categorías de tiro (lanes / sin licencia) el calibre es obligatorio
    // — igual que en la reserva pública y en la app
    const selectedCat = categories.find((c) => c.id === categoryId);
    const asksCaliber = selectedCat && ['lane', 'no-license-shooting'].includes(selectedCat.type);
    const finalCalibers = [
      ...selectedCalibers,
      ...otherCaliber.split(',').map((s) => s.trim()).filter(Boolean),
    ];
    if (asksCaliber && finalCalibers.length === 0) {
      setFormError('Indica al menos un calibre (requerido para el registro del campo de tiro).');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const endpoint = editingId
        ? `${FUNCTIONS_BASE}/updateRecurringReservation`
        : `${FUNCTIONS_BASE}/createRecurringReservation`;
      const r = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(editingId ? { id: editingId } : {}),
          memberEmail,
          memberPhone,
          categoryId,
          lanes: selectedLaneIds,
          laneId: selectedLaneIds[0] || null, // compat con backend viejo
          startTime,
          durationMinutes: Number(durationMinutes),
          daysOfWeek: dows,
          startDate,
          endDate,
          reason,
          calibers: [
            ...selectedCalibers,
            ...otherCaliber.split(',').map((s) => s.trim()).filter(Boolean),
          ],
        }),
      });
      const data = await r.json();
      if (r.ok) {
        setMessage({
          type: 'success',
          text: editingId
            ? 'Reservación recurrente actualizada.'
            : 'Reservación recurrente creada. Se generarán automáticamente cada noche.',
        });
        handleClose();
        fetchData();
      } else {
        setFormError(data.error || 'Error.');
      }
    } catch (e) {
      setFormError('Error de red.');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta reservación recurrente? Las ya generadas seguirán existiendo.')) return;
    try {
      await fetch(`${FUNCTIONS_BASE}/deleteRecurringReservation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      fetchData();
    } catch (e) { console.error(e); }
  };

  return (
    <>
      <Helmet><title>Reservaciones Recurrentes | BBTC</title></Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
          <Typography variant="h4">Reservaciones Recurrentes</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
            Añadir recurrente
          </Button>
        </Stack>

        {message && <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Miembro</TableCell>
                  <TableCell>Hora</TableCell>
                  <TableCell>Días</TableCell>
                  <TableCell>Rango</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {recurring.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>Sin recurrentes activas.</TableCell></TableRow>
                ) : recurring.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.memberEmail}</TableCell>
                    <TableCell>{r.startTime} ({r.durationMinutes} min)</TableCell>
                    <TableCell>{(r.daysOfWeek || []).map((d) => DAYS[d]).join(', ')}</TableCell>
                    <TableCell>{r.startDate} → {r.endDate}</TableCell>
                    <TableCell>{r.reason || '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleEdit(r)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(r.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Editar reservación recurrente' : 'Crear reservación recurrente'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Email del miembro" value={memberEmail} onChange={(e) => setMemberEmail(e.target.value)} fullWidth />
              <TextField label="Teléfono (opcional)" value={memberPhone} onChange={(e) => setMemberPhone(e.target.value)} fullWidth />
            </Stack>
            <TextField select label="Categoría" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} fullWidth>
              {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Lanes (opcional, puedes elegir varios)
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {filteredLanes.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    {categoryId
                      ? 'No hay lanes asociados a esta categoría.'
                      : 'Selecciona una categoría primero para ver sus lanes.'}
                  </Typography>
                ) : (
                  filteredLanes.map((l) => {
                    const selected = selectedLaneIds.includes(l.id);
                    return (
                      <Button
                        key={l.id}
                        variant={selected ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => toggleLaneSelected(l.id)}
                        sx={{ minWidth: 80, borderRadius: 2 }}
                      >
                        {l.name}
                      </Button>
                    );
                  })
                )}
              </Stack>
              {filteredLanes.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {selectedLaneIds.length === 0
                    ? 'Sin lanes seleccionados → cualquiera disponible'
                    : `${selectedLaneIds.length} lane(s) seleccionado(s)`}
                </Typography>
              )}
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Hora inicio" type="time" InputLabelProps={{ shrink: true }} value={startTime} onChange={(e) => setStartTime(e.target.value)} fullWidth />
              <TextField label="Duración (min)" type="number" value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Fecha inicio" type="date" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} fullWidth />
              <TextField label="Fecha fin" type="date" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} fullWidth />
            </Stack>
            <Divider />
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Días de la semana</Typography>
                <ButtonGroup size="small" variant="outlined">
                  <Button onClick={() => setDows([0, 1, 2, 3, 4, 5, 6])}>Todos</Button>
                  <Button onClick={() => setDows([1, 2, 3, 4, 5])}>Lun–Vie</Button>
                  <Button onClick={() => setDows([0, 6])}>Sáb–Dom</Button>
                  <Button onClick={() => setDows([])}>Limpiar</Button>
                </ButtonGroup>
              </Stack>
              <ToggleButtonGroup
                value={selectedDows}
                onChange={handleDowToggleChange}
                aria-label="días de la semana"
                size="small"
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                {DAYS.map((d, i) => (
                  <ToggleButton
                    key={i}
                    value={i}
                    sx={{
                      minWidth: 56,
                      borderRadius: '8px !important',
                      border: '1px solid rgba(0,0,0,0.12) !important',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': { bgcolor: 'primary.dark' },
                      },
                    }}
                  >
                    {d}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Click para seleccionar varios. Usa los botones de arriba para presets rápidos.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Calibre(s) que utiliza
                {(() => {
                  const c = categories.find((x) => x.id === categoryId);
                  return c && ['lane', 'no-license-shooting'].includes(c.type) ? ' (requerido)' : '';
                })()}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Se anotan en el registro del campo de tiro en cada visita generada por esta regla.
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {CALIBER_OPTIONS.map((cal) => (
                  <Chip
                    key={cal}
                    label={cal}
                    clickable
                    onClick={() => toggleCaliber(cal)}
                    color={selectedCalibers.includes(cal) ? 'error' : 'default'}
                    variant={selectedCalibers.includes(cal) ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 600 }}
                  />
                ))}
              </Stack>
              <TextField
                label="Otro calibre"
                value={otherCaliber}
                onChange={(e) => setOtherCaliber(e.target.value)}
                size="small"
                sx={{ mt: 2 }}
                placeholder="Ej: 10MM"
              />
            </Box>
            <TextField label="Motivo" value={reason} onChange={(e) => setReason(e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : (editingId ? 'Guardar cambios' : 'Crear recurrente')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
